import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ReceiptText, ShieldCheck, Truck, Sparkles, ChevronDown, TrendingUp, MessageCircle, Check, ArrowRight, Star, AlertTriangle, Phone, Mail, Activity, PackageCheck } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
type KpiItem = {
  value: string;
  label: string;
};
type Proof = {
  kind: 'chat';
  line: string;
} | {
  kind: 'invoice';
  label: string;
  original: string;
  corrected: string;
} | {
  kind: 'carriers';
  rows: Array<{
    name: string;
    cost: string;
    logo?: string;
    cheapest?: boolean;
  }>;
} | {
  kind: 'checklist';
  items: string[];
} | {
  kind: 'timeslots';
  slots: string[];
  selected: string;
} | {
  kind: 'chips';
  chips: string[];
  activeIndex: number;
} | {
  kind: 'branch';
  positive: string;
  negative: string;
} | {
  kind: 'pipeline';
  steps: string[];
} | {
  kind: 'coupon';
  discount: string;
  cta: string;
} | {
  kind: 'tracking-log';
  entries: Array<{
    time: string;
    status: string;
    anomaly?: boolean;
  }>;
  unblockChannels: string[];
} | {
  kind: 'multichannel-chat';
  channels: Array<{
    name: string;
    logo: string;
    status: 'active' | 'no-response';
  }>;
  messages: Array<{
    from: 'ai' | 'user';
    text: string;
    confirmed?: boolean;
  }>;
};
type Module = {
  name: string;
  description: string;
  problem: string;
  problemChips?: string[];
  howItWorks: string;
  kpi: KpiItem[];
  proof: Proof;
};
type Phase = {
  id: string;
  index: string;
  label: string;
  description: string;
  icon: ComponentType<{
    className?: string;
  }>;
  modules: Module[];
};
const phases: Phase[] = [{
  id: 'controllo-costi',
  index: '01',
  label: 'Controllo dei costi',
  description: 'Aisac analizza fatture e contratti per recuperare quanto non dovuto e suggerisce il vettore migliore per ogni ordine, prima ancora di stampare la LDV.',
  icon: ReceiptText,
  modules: [{
    name: 'Verifica dei sovrapprezzi e dei costi nascosti',
    description: 'AISAC Invoice Check — verifica automatica delle fatture del corriere rispetto al contratto e alle spedizioni effettive.',
    problem: 'I corrieri inseriscono in fattura sovrapprezzi fuori dalla tariffa concordata. In media, il 12% del costo di spedizione resta opaco e non validato.',
    problemChips: ['Supplemento carburante', 'Peso volumetrico', 'Sovrapprezzo di emergenza'],
    howItWorks: 'Aisac incrocia ogni fattura con contratto e spedizioni reali, segnala automaticamente gli scostamenti e produce le contestazioni pronte per il corriere.',
    kpi: [{
      value: '−25%',
      label: 'costi fatturati e non concordati'
    }],
    proof: {
      kind: 'invoice',
      label: 'Supplemento ZTL non concordato',
      original: '€ 1.840',
      corrected: '€ 0'
    }
  }, {
    name: 'Predizione del costo di spedizione',
    description: 'AISAC Cost Prediction — stima del costo finale di ogni spedizione per ciascun corriere disponibile.',
    problem: 'La scelta del corriere si basa sul prezzo, perché è l\u2019unico dato sempre disponibile. Ma la decisione migliore dipende anche da velocità, affidabilità sulla rotta, capacità di gestire spedizioni speciali — variabili che oggi nessuno mette a confronto.',
    problemChips: ['Costo per corriere inefficiente'],
    howItWorks: 'Per ogni ordine, l\u2019AI stima il costo finale atteso per ciascun corriere e suggerisce la scelta più conveniente prima della stampa della LDV.',
    kpi: [{
      value: '−25%',
      label: 'di costo medio salvato per spedizione'
    }, {
      value: '+50%',
      label: 'di efficienza logistica grazie alla selezione del corriere più adatto'
    }],
    proof: {
      kind: 'carriers',
      rows: [{
        name: 'BRT',
        cost: '€ 4,20',
        logo: 'https://cdn.magicpatterns.com/uploads/1VM8KymEiuxA1zV1s71CTR/BRT_logo_cropped.png',
        cheapest: true
      }, {
        name: 'GLS',
        cost: '€ 4,65',
        logo: 'https://cdn.magicpatterns.com/uploads/xhQQALLqNSDBYGfz64FK4u/GLS_Logo_2021.svg.png'
      }, {
        name: 'FedEx',
        cost: '€ 5,10',
        logo: 'https://cdn.magicpatterns.com/uploads/5ynV5BhKCakb4DDVPSKjjS/FedEx-Logo.png'
      }]
    }
  }]
}, {
  id: 'pre-spedizione',
  index: '02',
  label: 'Pre-spedizione',
  description: 'Bonifica preventiva di numeri, indirizzi e ordini per evitare spedizioni che partono già destinate a fallire.',
  icon: ShieldCheck,
  modules: [{
    name: 'Validazione automatica delle informazioni di spedizione',
    description: 'AISAC Data Validation — validazione e arricchimento dei dati di spedizione prima che il pacco lasci il magazzino.',
    problem: 'Numeri sbagliati, indirizzi incompleti, ordini non confermati: ogni dato mancante a monte si traduce a valle in giacenze e riconsegne.',
    problemChips: ['Indirizzo mancante', 'Numero errato'],
    howItWorks: 'Prima della spedizione, Aisac controlla che il numero sia raggiungibile, l\u2019indirizzo corretto e l\u2019ordine confermato. Quando un dato manca, lo ricerca prima nei tuoi sistemi e solo poi lo chiede al cliente. Solo allora il pacco lascia il magazzino.',
    kpi: [{
      value: '90%',
      label: 'di dati validati pre-consegna'
    }, {
      value: '−50%',
      label: 'di mancate consegne a causa di dati errati'
    }],
    proof: {
      kind: 'checklist',
      items: ['Numero verificato', 'Indirizzo confermato', 'Ordine confermato']
    }
  }]
}, {
  id: 'in-transito',
  index: '03',
  label: 'In transito',
  description: 'Aisac segue ogni spedizione minuto per minuto, comunica al cliente in modo affidabile e interviene quando qualcosa va storto.',
  icon: Truck,
  modules: [{
    name: 'Tracciamento spedizione in tempo reale',
    description: 'AISAC Realtime Tracking — monitoraggio automatico di ogni spedizione ogni 15 minuti, con intercettazione immediata di blocchi e anomalie.',
    problem: 'Una spedizione può non partire, fermarsi in transito, restare in un hub per giorni. L\u2019anomalia diventa visibile solo quando il cliente chiama — e a quel punto recuperare la consegna costa più del danno originario.',
    problemChips: ['Spedizione ferma per anomalia'],
    howItWorks: 'Aisac interroga il corriere ogni 15 minuti e registra lo stato di ogni spedizione in un log continuo. Appena rileva un\u2019anomalia (blocco, mancato avanzamento, indirizzo da chiarire), apre uno sblocco proattivo in multi-channel, fino a rimettere il pacco in viaggio.',
    kpi: [{
      value: '100%',
      label: 'intercettazione di anomalie'
    }, {
      value: '90%',
      label: 'anticipazione mancanza del destinatario'
    }],
    proof: {
      kind: 'tracking-log',
      entries: [{
        time: '08:00',
        status: 'In transito · Hub Bologna'
      }, {
        time: '08:15',
        status: 'In transito · Hub Bologna'
      }, {
        time: '08:30',
        status: 'Fermo in deposito',
        anomaly: true
      }, {
        time: '08:32',
        status: 'Sblocco avviato'
      }],
      unblockChannels: ['WhatsApp', 'SMS', 'Smart Call']
    }
  }, {
    name: 'Gestione attiva del giorno di consegna',
    description: 'AISAC Day Check — coordinamento del giorno di consegna tra cliente, corriere e brand.',
    problem: 'Il primo tentativo di consegna fallisce in un\u2019ampia percentuale dei casi, quasi sempre per assenza del destinatario: non è informato, non è in casa, non risponde al corriere.',
    problemChips: ['Spedizione non consegnata', 'Mancanza destinatario'],
    howItWorks: 'Il giorno della consegna, Aisac contatta il cliente sui canali più adatti con la stima della fascia oraria. Il cliente può confermare, riprogrammare o variare l\u2019indirizzo.',
    kpi: [{
      value: '84%',
      label: 'consegna al primo tentativo'
    }, {
      value: '95%',
      label: 'tasso di risposta grazie a comunicazione multi-channel'
    }],
    proof: {
      kind: 'multichannel-chat',
      channels: [{
        name: 'Smart Call',
        logo: 'https://cdn.magicpatterns.com/uploads/6tEqaPdymAmivLGUmyvHsV/phone-call-icon-answer-accept-call-icon-with-green-button-contact-us-telephone-sign-yes-button-incoming-call-icon-vector.jpg',
        status: 'no-response'
      }, {
        name: 'Telegram',
        logo: 'https://cdn.magicpatterns.com/uploads/uECfEQcijeV2QYLrZMkcyg/Telegram_2019_Logo.svg.png',
        status: 'no-response'
      }, {
        name: 'Email',
        logo: 'https://cdn.magicpatterns.com/uploads/cRF6PEqScQjVAeFst51kRE/4202011emailgmaillogomailsocialsocialmedia-115677_115624.webp',
        status: 'no-response'
      }, {
        name: 'WhatsApp',
        logo: 'https://cdn.magicpatterns.com/uploads/tuVCDWMVDjUzGprMZznQmx/whatsapp_logo_icon_186881.webp',
        status: 'active'
      }],
      messages: [{
        from: 'ai',
        text: 'Ciao Marco, il tuo ordine #2841 è in consegna oggi. Quale fascia preferisci: 10–12, 14–16 o 16–18?'
      }, {
        from: 'user',
        text: 'Meglio 16–18, grazie 🙏'
      }, {
        from: 'ai',
        text: 'Perfetto, fascia aggiornata a 16–18. A più tardi!',
        confirmed: true
      }]
    }
  }, {
    name: 'Risoluzione delle giacenze',
    description: 'AISAC NDR Resolver — risoluzione automatizzata delle giacenze in deposito tramite contatto con il cliente.',
    problem: 'Una giacenza si paga su tre fronti: deposito, riconsegna, reputazione col cliente. E lo sblocco manuale assorbe ore di customer care.',
    problemChips: ['Costo di deposito', 'Costo di ri-consegna', 'Costo Customer Care'],
    howItWorks: 'Aisac contatta il cliente sul canale più adatto e gestisce in autonomia svincolo diretto, riconsegna, variazione indirizzo o ritiro in filiale.',
    kpi: [{
      value: '−66,7%',
      label: 'riduzione del tempo di svincolo (da 36h a 12h)'
    }],
    proof: {
      kind: 'chips',
      chips: ['Svincolo', 'Riconsegna', 'Variazione', 'Ritiro'],
      activeIndex: 0
    }
  }]
}, {
  id: 'post-vendita',
  index: '04',
  label: 'Post-vendita',
  description: 'Il momento dopo la consegna è quello di massima propensione al riacquisto: Aisac lo presidia con feedback, resi e azioni di retention.',
  icon: Sparkles,
  modules: [{
    name: 'Raccolta feedback post-consegna',
    description: 'AISAC Review Manager — raccolta strutturata del feedback post-consegna e gestione differenziata.',
    problem: 'Le recensioni negative diventano pubbliche prima che il problema venga intercettato. I clienti soddisfatti, invece, raramente lasciano recensioni di propria iniziativa.',
    problemChips: ['Recensioni non controllate'],
    howItWorks: 'Subito dopo la consegna, Aisac chiede un feedback strutturato e smista in automatico: se positivo, invita il cliente a lasciare una recensione pubblica; se negativo, apre un alert immediato sul CRM per il recupero del cliente.',
    kpi: [{
      value: '78%',
      label: 'rilascio feedback a 30 giorni'
    }, {
      value: '68%',
      label: 'recensioni ≥4 stelle'
    }],
    proof: {
      kind: 'branch',
      positive: 'Recensione pubblica',
      negative: 'CRM alert'
    }
  }, {
    name: 'Gestione automatica dei resi',
    description: 'AISAC Return Management — gestione end-to-end del reso, dalla richiesta all\u2019esito finale.',
    problem: 'Il processo di reso è quasi sempre manuale: richieste via email, autorizzazioni gestite caso per caso, nessuna visibilità in tempo reale sullo stato.',
    problemChips: ['Costo di gestione del reso'],
    howItWorks: 'Aisac gestisce in autonomia ogni passaggio del reso: condizioni del prodotto, emissione RMA, ritiro, tracking del rientro, comunicazione al cliente.',
    kpi: [{
      value: '95%',
      label: 'tempo manuale di gestione reso risparmiato'
    }],
    proof: {
      kind: 'pipeline',
      steps: ['Richiesta', 'RMA', 'Ritiro', 'Esito']
    }
  }, {
    name: 'Up-selling automatico post-acquisto',
    description: 'AISAC Conversational Selling — recupero dei clienti insoddisfatti e attivazione del riacquisto subito dopo la consegna.',
    problem: 'I giorni subito dopo la consegna sono quelli in cui il cliente è più disposto a comprare di nuovo. Spesso però nessuno lo contatta, e l\u2019occasione passa.',
    problemChips: ['Mancata opportunità', 'Potenziale Up-Selling'],
    howItWorks: 'Aisac scrive al cliente subito dopo la consegna e gli chiede com\u2019è andata. Se è soddisfatto, lo guida a lasciare una recensione e gli propone un coupon per il prossimo ordine. Se non lo è, ascolta il problema e attiva un reso o uno sconto per recuperarlo.',
    kpi: [{
      value: '17%',
      label: 'di utilizzo del coupon entro 30 giorni'
    }, {
      value: '30%',
      label: 'di utilizzo del coupon entro 60 giorni'
    }],
    proof: {
      kind: 'coupon',
      discount: '−15%',
      cta: 'Usa ora'
    }
  }]
}];
/* ─────────────────────────────────────────────────────────────
   PROOF WIDGET — module-specific visual proof of mechanism
   ───────────────────────────────────────────────────────────── */
interface ProofWidgetProps {
  proof: Proof;
}
function ProofWidget({
  proof
}: ProofWidgetProps) {
  const eyebrowStyle = {
    letterSpacing: '0.14em'
  } as const;
  const frameClass = 'rounded-xl border border-default bg-[#0a0a0a] p-5 lg:p-6';
  switch (proof.kind) {
    case 'chat':
      return <div className={frameClass} data-id="element-342">
          <div className="flex items-center gap-2 mb-4" data-id="element-343">
            <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{
            background: 'rgba(37,211,102,0.15)'
          }} data-id="element-344">
              <MessageCircle className="w-3.5 h-3.5" style={{
              color: '#25d366'
            }} aria-hidden="true" data-id="element-345" />
            </span>
            <span className="text-mono-tiny color-ink-tertiary uppercase" style={eyebrowStyle} data-id="element-346">
              WhatsApp · Aisac
            </span>
          </div>
          <div className="rounded-2xl rounded-tl-sm px-4 py-3" style={{
          background: 'rgba(37,211,102,0.12)',
          border: '1px solid rgba(37,211,102,0.25)'
        }} data-id="element-347">
            <p className="text-body-sm color-ink-primary leading-relaxed" data-id="element-348">
              {proof.line}
            </p>
          </div>
        </div>;
    case 'invoice':
      return <div className={frameClass} data-id="element-349">
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-4" style={eyebrowStyle} data-id="element-350">
            Riga fattura corriere
          </div>
          <div className="flex items-start justify-between gap-4 py-3 border-t border-default" data-id="element-351">
            <span className="leading-snug max-w-[18ch]" style={{
            color: 'var(--accent-500)',
            fontWeight: 500
          }} data-id="element-352">
              {proof.label}
            </span>
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-353">
              <span className="text-body-sm tabular-nums color-ink-tertiary line-through" data-id="element-354">
                {proof.original}
              </span>
              <ArrowRight className="w-4 h-4 color-ink-tertiary" aria-hidden="true" data-id="element-355" />
              <span className="text-body-md tabular-nums" style={{
              color: 'var(--accent-500)',
              fontWeight: 500
            }} data-id="element-356">
                {proof.corrected}
              </span>
            </div>
          </div>
        </div>;
    case 'carriers':
      return <div className={frameClass} data-id="element-357">
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-3" style={eyebrowStyle} data-id="element-358">
            Stima per corriere
          </div>
          <ul data-id="element-359">
            {proof.rows.map((r, i) => <li key={r.name} className={`flex items-center justify-between py-2.5 ${i > 0 ? 'border-t border-default' : ''}`} data-id="element-360">
                <div className="flex items-center gap-3" data-id="element-361">
                  <span aria-hidden="true" className="w-4 h-4 flex items-center justify-center flex-shrink-0" data-id="element-362">
                    {r.cheapest ? <Check className="w-4 h-4" style={{
                  color: 'var(--accent-500)'
                }} data-id="element-363" /> : null}
                  </span>
                  {r.logo && <span aria-hidden="true" className="inline-flex items-center justify-center rounded-md flex-shrink-0" style={{
                width: 36,
                height: 22,
                background: '#ffffff',
                padding: '2px 4px'
              }} data-id="element-364">
                      <img src={r.logo} alt="" className="max-w-full max-h-full object-contain" data-id="element-365" />
                    </span>}
                  <span className={`text-body-sm ${r.cheapest ? 'color-ink-primary' : 'color-ink-secondary'}`} style={{
                fontWeight: r.cheapest ? 500 : 400
              }} data-id="element-366">
                    {r.name}
                  </span>
                </div>
                <span className="text-body-sm tabular-nums" style={{
              color: r.cheapest ? 'var(--accent-500)' : 'rgba(255,255,255,0.6)',
              fontWeight: r.cheapest ? 500 : 400
            }} data-id="element-367">
                  {r.cost}
                </span>
              </li>)}
          </ul>
        </div>;
    case 'checklist':
      return <div className={frameClass} data-id="element-368">
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-4" style={eyebrowStyle} data-id="element-369">
            Pre-spedizione
          </div>
          <ul className="space-y-3" data-id="element-370">
            {proof.items.map(it => <li key={it} className="flex items-center gap-3" data-id="element-371">
                <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{
              background: 'rgba(212,255,58,0.15)',
              border: '1px solid var(--accent-500)'
            }} aria-hidden="true" data-id="element-372">
                  <Check className="w-3 h-3" style={{
                color: 'var(--accent-500)'
              }} data-id="element-373" />
                </span>
                <span className="text-body-sm color-ink-primary" data-id="element-374">{it}</span>
              </li>)}
          </ul>
        </div>;
    case 'timeslots':
      return <div className={frameClass} data-id="element-375">
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-3" style={eyebrowStyle} data-id="element-376">
            Fascia di consegna
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-id="element-377">
            {proof.slots.map(slot => {
            const sel = slot === proof.selected;
            return <span key={slot} className="text-body-sm px-3 py-1.5 rounded-full tabular-nums" style={{
              background: sel ? 'var(--accent-500)' : 'transparent',
              color: sel ? '#000' : 'rgba(255,255,255,0.6)',
              border: sel ? '1px solid var(--accent-500)' : '1px solid rgba(255,255,255,0.18)',
              fontWeight: sel ? 500 : 400
            }} data-id="element-378">
                  {slot}
                </span>;
          })}
          </div>
          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-default" data-id="element-379">
            <MessageCircle className="w-4 h-4" style={{
            color: '#25d366'
          }} aria-hidden="true" data-id="element-380" />
            <span className="text-mono-tiny color-ink-tertiary uppercase" style={eyebrowStyle} data-id="element-381">
              Confermato via WhatsApp
            </span>
          </div>
        </div>;
    case 'chips':
      return <div className={frameClass} data-id="element-382">
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-4" style={eyebrowStyle} data-id="element-383">
            Risoluzione giacenza
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-384">
            {proof.chips.map((c, i) => {
            const active = i === proof.activeIndex;
            return <span key={c} className="text-body-sm px-3 py-1.5 rounded-full" style={{
              background: active ? 'var(--accent-500)' : 'transparent',
              color: active ? '#000' : 'rgba(255,255,255,0.6)',
              border: active ? '1px solid var(--accent-500)' : '1px solid rgba(255,255,255,0.18)',
              fontWeight: active ? 500 : 400
            }} data-id="element-385">
                  {c}
                </span>;
          })}
          </div>
        </div>;
    case 'branch':
      return <div className={frameClass} data-id="element-386">
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-4" style={eyebrowStyle} data-id="element-387">
            Smistamento feedback
          </div>
          <div className="relative grid items-center" style={{
          gridTemplateColumns: '44px 48px 1fr',
          rowGap: 12
        }} data-id="element-388">
            {/* Source node — pacco consegnato, spans both rows */}
            <div className="row-span-2 flex items-center justify-center self-center relative z-10" style={{
            height: '100%'
          }} data-id="element-389">
              <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{
              background: '#0a0a0a',
              border: '1px solid rgba(255,255,255,0.25)',
              boxShadow: '0 0 0 4px #0a0a0a'
            }} aria-hidden="true" data-id="element-390">
                <PackageCheck className="w-5 h-5" style={{
                color: 'rgba(255,255,255,0.85)'
              }} data-id="element-391" />
              </div>
            </div>

            {/* SVG connectors — continuous lines from source to each card */}
            <svg className="row-span-2 self-stretch pointer-events-none" width="48" height="100%" viewBox="0 0 48 100" preserveAspectRatio="none" aria-hidden="true" style={{
            display: 'block'
          }} data-id="element-392">
              {/* Positive branch: from left center, right, up, right to card */}
              <path d="M 0 50 L 24 50 L 24 22 L 48 22" fill="none" stroke="var(--accent-500)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" data-id="element-393" />
              {/* Negative branch: from left center, right, down, right to card */}
              <path d="M 0 50 L 24 50 L 24 78 L 48 78" fill="none" stroke="var(--warm-500)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" data-id="element-394" />
              {/* Junction dot */}
              <circle cx="24" cy="50" r="2.5" fill="rgba(255,255,255,0.6)" data-id="element-395" />
            </svg>

            {/* Positive outcome card */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-md relative z-10" style={{
            border: '1px solid var(--accent-500)',
            background: 'rgba(212,255,58,0.08)'
          }} data-id="element-396">
              <Star className="w-4 h-4 flex-shrink-0" style={{
              color: 'var(--accent-500)'
            }} aria-hidden="true" data-id="element-397" />
              <span className="text-body-sm color-ink-primary" data-id="element-398">
                {proof.positive}
              </span>
            </div>

            {/* Negative outcome card */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-md relative z-10" style={{
            border: '1px solid var(--warm-500)',
            background: 'rgba(255,107,71,0.06)'
          }} data-id="element-399">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{
              color: 'var(--warm-500)'
            }} aria-hidden="true" data-id="element-400" />
              <span className="text-body-sm color-ink-primary" data-id="element-401">
                {proof.negative}
              </span>
            </div>
          </div>
        </div>;
    case 'pipeline':
      return <div className={frameClass} data-id="element-402">
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-4" style={eyebrowStyle} data-id="element-403">
            Flusso reso
          </div>
          <ol className="flex items-center gap-2 overflow-x-auto" data-id="element-404">
            {proof.steps.map((s, i) => <Fragment key={s} data-id="element-405">
                <li className="flex items-center gap-2 flex-shrink-0" data-id="element-406">
                  <span className="w-2 h-2 rounded-full" style={{
                background: 'var(--accent-500)'
              }} aria-hidden="true" data-id="element-407" />
                  <span className="text-body-sm color-ink-primary whitespace-nowrap" data-id="element-408">
                    {s}
                  </span>
                </li>
                {i < proof.steps.length - 1 && <span className="h-px flex-1 min-w-[16px]" style={{
              background: 'rgba(255,255,255,0.18)'
            }} aria-hidden="true" data-id="element-409" />}
              </Fragment>)}
          </ol>
        </div>;
    case 'tracking-log':
      return <div className={frameClass} data-id="element-410">
          <div className="flex items-center justify-between mb-4" data-id="element-411">
            <div className="text-mono-tiny color-ink-tertiary uppercase flex items-center gap-2" style={eyebrowStyle} data-id="element-412">
              <Activity className="w-3.5 h-3.5" style={{
              color: 'var(--accent-500)'
            }} aria-hidden="true" data-id="element-413" />
              Log tracciamento · ogni 15 min
            </div>
            <span className="inline-flex items-center gap-1.5 text-mono-tiny uppercase" style={{
            color: 'var(--accent-500)',
            letterSpacing: '0.14em'
          }} data-id="element-414">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              background: 'var(--accent-500)',
              boxShadow: '0 0 8px var(--accent-500)'
            }} aria-hidden="true" data-id="element-415" />
              Live
            </span>
          </div>

          <ol className="relative pl-4 mb-4" data-id="element-416">
            <span className="absolute left-[5px] top-1 bottom-1 w-px" style={{
            background: 'rgba(255,255,255,0.12)'
          }} aria-hidden="true" data-id="element-417" />
            {proof.entries.map((entry, i) => {
            const isAnomaly = entry.anomaly;
            const isLast = i === proof.entries.length - 1;
            const isResolved = isLast && proof.entries.some(e => e.anomaly);
            return <li key={`${entry.time}-${i}`} className="relative flex items-start gap-3 py-1.5" data-id="element-418">
                  <span className="absolute -left-[1px] w-3 h-3 rounded-full mt-1.5" style={{
                background: isAnomaly ? 'var(--warm-500)' : isResolved ? 'var(--accent-500)' : '#0a0a0a',
                border: isAnomaly ? '2px solid var(--warm-500)' : isResolved ? '2px solid var(--accent-500)' : '2px solid rgba(255,255,255,0.25)',
                boxShadow: isAnomaly ? '0 0 0 3px rgba(255,107,71,0.18)' : 'none'
              }} aria-hidden="true" data-id="element-419" />
                  <span className="text-mono-tiny tabular-nums color-ink-tertiary pt-0.5 flex-shrink-0 w-[44px]" style={{
                letterSpacing: '0.04em'
              }} data-id="element-420">
                    {entry.time}
                  </span>
                  <span className={`text-body-sm leading-snug ${isAnomaly ? '' : isResolved ? '' : 'color-ink-secondary'}`} style={{
                color: isAnomaly ? 'var(--warm-500)' : isResolved ? 'var(--accent-500)' : undefined,
                fontWeight: isAnomaly || isResolved ? 500 : 400
              }} data-id="element-421">
                    {isAnomaly && <AlertTriangle className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" aria-hidden="true" data-id="element-422" />}
                    {entry.status}
                  </span>
                </li>;
          })}
          </ol>

          <div className="pt-3 border-t border-default" data-id="element-423">
            <div className="text-mono-tiny color-ink-tertiary uppercase mb-2" style={eyebrowStyle} data-id="element-424">
              Sblocco multi-channel
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-425">
              {proof.unblockChannels.map(ch => {
              const isWa = /whatsapp/i.test(ch);
              const isCall = /call|chiamata/i.test(ch);
              const Icon = isWa ? MessageCircle : isCall ? Phone : Mail;
              const iconColor = isWa ? '#25d366' : 'var(--accent-500)';
              return <span key={ch} className="inline-flex items-center gap-1.5 text-body-sm px-2.5 py-1 rounded-full" style={{
                background: 'rgba(212,255,58,0.08)',
                border: '1px solid var(--accent-500)',
                color: 'rgba(255,255,255,0.9)'
              }} data-id="element-426">
                    <Icon className="w-3.5 h-3.5" style={{
                  color: iconColor
                }} aria-hidden="true" data-id="element-427" />
                    {ch}
                  </span>;
            })}
            </div>
          </div>
        </div>;
    case 'multichannel-chat':
      return <div className={frameClass} data-id="element-428">
          {/* ── Top row: channel logos with status ── */}
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-3" style={eyebrowStyle} data-id="element-429">
            Contatto multi-channel
          </div>
          <div className="flex items-end justify-between gap-2 mb-5 pb-5 border-b border-default" data-id="element-430">
            {proof.channels.map(ch => {
            const isActive = ch.status === 'active';
            return <div key={ch.name} className="flex flex-col items-center gap-1.5 flex-1 min-w-0" data-id="element-431">
                  <span className="relative inline-flex items-center justify-center rounded-full" style={{
                width: 40,
                height: 40,
                background: '#ffffff',
                padding: 4,
                opacity: isActive ? 1 : 0.35,
                filter: isActive ? 'none' : 'grayscale(0.7)',
                boxShadow: isActive ? '0 0 0 2px var(--accent-500), 0 0 16px rgba(212,255,58,0.45)' : 'none',
                transition: 'all 0.2s ease'
              }} aria-hidden="true" data-id="element-432">
                    <img src={ch.logo} alt="" className="w-full h-full object-contain rounded-full" data-id="element-433" />
                    {!isActive && <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full" style={{
                  width: 14,
                  height: 14,
                  background: 'var(--warm-500)',
                  border: '1.5px solid #0a0a0a'
                }} data-id="element-434">
                        <span className="block w-1.5 h-px" style={{
                    background: '#0a0a0a'
                  }} data-id="element-435" />
                      </span>}
                  </span>
                  <span className="text-mono-tiny uppercase truncate w-full text-center" style={{
                color: isActive ? 'var(--accent-500)' : 'rgba(255,255,255,0.4)',
                letterSpacing: '0.08em',
                fontWeight: isActive ? 500 : 400
              }} data-id="element-436">
                    {isActive ? 'Risposta' : 'Nessuna'}
                  </span>
                </div>;
          })}
          </div>

          {/* ── WhatsApp conversation ── */}
          <div className="flex items-center gap-2 mb-3" data-id="element-437">
            <span className="w-6 h-6 rounded-full inline-flex items-center justify-center" style={{
            background: '#ffffff',
            padding: 2
          }} aria-hidden="true" data-id="element-438">
              <img src="https://cdn.magicpatterns.com/uploads/tuVCDWMVDjUzGprMZznQmx/whatsapp_logo_icon_186881.webp" alt="" className="w-full h-full object-contain rounded-full" data-id="element-439" />
            </span>
            <span className="text-mono-tiny color-ink-tertiary uppercase" style={eyebrowStyle} data-id="element-440">
              WhatsApp · Aisac
            </span>
          </div>

          <div className="rounded-xl p-3 space-y-2" style={{
          background: 'linear-gradient(180deg, rgba(11,20,18,0.6) 0%, rgba(11,20,18,0.3) 100%)',
          border: '1px solid rgba(37,211,102,0.18)'
        }} data-id="element-441">
            {proof.messages.map((msg, i) => {
            const isAi = msg.from === 'ai';
            return <div key={i} className={`flex ${isAi ? 'justify-start' : 'justify-end'}`} data-id="element-442">
                  <div className={`max-w-[82%] px-3 py-2 ${isAi ? 'rounded-2xl rounded-tl-sm' : 'rounded-2xl rounded-tr-sm'}`} style={{
                background: isAi ? 'rgba(255,255,255,0.06)' : 'rgba(37,211,102,0.18)',
                border: isAi ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(37,211,102,0.32)'
              }} data-id="element-443">
                    <p className="text-body-sm color-ink-primary leading-relaxed" data-id="element-444">
                      {msg.text}
                    </p>
                    {msg.confirmed && <div className="mt-1.5 inline-flex items-center gap-1 text-mono-tiny uppercase" style={{
                  color: 'var(--accent-500)',
                  letterSpacing: '0.12em'
                }} data-id="element-445">
                        <Check className="w-3 h-3" aria-hidden="true" data-id="element-446" />
                        Fascia aggiornata dall'AI
                      </div>}
                  </div>
                </div>;
          })}
          </div>
        </div>;
    case 'coupon':
      return <div className={frameClass} data-id="element-447">
          <div className="text-mono-tiny color-ink-tertiary uppercase mb-4" style={eyebrowStyle} data-id="element-448">
            Coupon WhatsApp
          </div>
          <div className="rounded-lg p-4 flex items-center justify-between gap-4" style={{
          border: '1px dashed var(--accent-500)',
          background: 'rgba(212,255,58,0.06)'
        }} data-id="element-449">
            <div data-id="element-450">
              <div className="tabular-nums" style={{
              color: 'var(--accent-500)',
              fontFamily: 'var(--font-display)',
              fontSize: 32,
              fontWeight: 500,
              lineHeight: 1,
              letterSpacing: '-0.02em'
            }} data-id="element-451">
                {proof.discount}
              </div>
              <div className="text-mono-tiny color-ink-tertiary uppercase mt-1" style={eyebrowStyle} data-id="element-452">
                Sul prossimo ordine
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-body-sm px-3 py-1.5 rounded-full flex-shrink-0" style={{
            background: 'var(--accent-500)',
            color: '#000',
            fontWeight: 500
          }} data-id="element-453">
              {proof.cta}
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" data-id="element-454" />
            </span>
          </div>
        </div>;
    default:
      return null;
  }
}
/* ─────────────────────────────────────────────────────────────
   MODULE CARD — compact summary row + reveal-on-demand storyline
   ───────────────────────────────────────────────────────────── */
interface ModuleCardProps {
  module: Module;
  index: number;
}
function ModuleCard({
  module: m,
  index
}: ModuleCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = `module-panel-${index}-${m.name.replace(/\s+/g, '-')}`;
  return <motion.article initial={{
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.15
  }} transition={{
    duration: 0.4,
    delay: index * 0.04
  }} className="rounded-xl" style={{
    background: '#000000',
    border: '1px solid rgba(255,255,255,0.18)'
  }} data-id="element-455">
      {/* ── Summary row — name, KPI pill, chevron ── */}
      <button type="button" onClick={() => setIsOpen(v => !v)} aria-expanded={isOpen} aria-controls={panelId} className="group w-full text-left flex items-center gap-4 py-5 px-5 lg:px-6 cursor-pointer focus:outline-none" data-id="element-456">
        {/* Eyebrow + Name */}
        <div className="flex-1 min-w-0" data-id="element-457">
          <div className="text-mono-tiny uppercase mb-1.5" style={{
          color: 'var(--accent-500)',
          letterSpacing: '0.14em'
        }} data-id="element-458">
            {`Modulo ${String(index + 1).padStart(2, '0')}`}
          </div>
          <h4 className="color-ink-primary text-[18px] md:text-[20px] lg:text-[22px] font-[500] transition-colors duration-200 group-hover:color-accent truncate" style={{
          fontFamily: 'var(--font-display)',
          lineHeight: 1.2,
          letterSpacing: '-0.015em'
        }} data-id="element-459">
            {m.name}
          </h4>
        </div>

        {/* Toggle label + chevron — light gray, lowercase, sits next to the arrow */}
        <span className="hidden sm:inline-flex flex-shrink-0 items-center gap-2 transition-colors duration-200 group-hover:color-accent" data-id="element-460">
          <span className="text-body-md" style={{
          color: 'rgba(255,255,255,0.55)'
        }} data-id="element-461">
            {isOpen ? 'chiudi' : 'vedi di più'}
          </span>
          <ChevronDown aria-hidden="true" className="w-5 h-5 transition-transform duration-300" style={{
          color: 'rgba(255,255,255,0.55)',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
        }} data-id="element-462" />
        </span>

        {/* Mobile: chevron only */}
        <span aria-hidden="true" className="sm:hidden flex-shrink-0 w-8 h-8 flex items-center justify-center color-ink-secondary group-hover:color-accent transition-colors duration-200" data-id="element-463">
          <ChevronDown className="w-5 h-5 transition-transform duration-300" style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
        }} data-id="element-464" />
        </span>
      </button>

      {/* ── Reveal panel: 1-line lead + 3-step storyline ── */}
      <AnimatePresence initial={false} data-id="element-465">
        {isOpen && <motion.div id={panelId} key="reveal" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        height: {
          duration: 0.32,
          ease: [0.16, 1, 0.3, 1]
        },
        opacity: {
          duration: 0.22
        }
      }} style={{
        overflow: 'hidden'
      }} data-id="element-466">
            <div className="px-5 lg:px-6 pb-8 lg:pb-10 pt-2" data-id="element-467">
              <div data-id="element-468">
                {/* ── TOP — IL PROBLEMA ── */}
                <div className="mb-8 lg:mb-10" data-id="element-469">
                  <div className="text-mono-tiny uppercase mb-3" style={{
                color: 'var(--warm-500)',
                letterSpacing: '0.14em'
              }} data-id="element-470">
                    Il problema
                  </div>
                  <blockquote className="pl-4 max-w-[60ch]" style={{
                borderLeft: '2px solid var(--warm-500)'
              }} data-id="element-471">
                    <p className="color-ink-primary leading-relaxed" style={{
                  fontSize: 'clamp(17px, 1.6vw, 19px)'
                }} data-id="element-472">
                      {m.problem}
                    </p>

                    {m.problemChips && m.problemChips.length > 0 && <ul className="mt-4 flex flex-wrap gap-2 list-none p-0" data-id="element-473">
                        {m.problemChips.map(chip => <li key={chip} className="text-body-sm px-3 py-1.5 rounded-full color-ink-secondary" style={{
                    border: '1px solid rgba(255,107,71,0.35)',
                    background: 'rgba(255,107,71,0.06)'
                  }} data-id="element-474">
                            {chip}
                          </li>)}
                      </ul>}
                  </blockquote>
                </div>

                {/* ── MIDDLE — COME FUNZIONA + PROOF WIDGET ── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 items-start mb-8 lg:mb-10" data-id="element-475">
                  <div data-id="element-476">
                    <div className="text-mono-tiny color-ink-secondary uppercase mb-3" style={{
                  letterSpacing: '0.14em'
                }} data-id="element-477">
                      Come funziona
                    </div>
                    <p className="text-body-md color-ink-primary leading-relaxed max-w-[44ch]" data-id="element-478">
                      {m.howItWorks}
                    </p>
                  </div>
                  <div data-id="element-479">
                    <ProofWidget proof={m.proof} data-id="element-480" />
                  </div>
                </div>

                {/* ── BOTTOM — BENEFICI ATTESI (climax) ── */}
                <div className="pt-6 lg:pt-8 border-t border-default" data-id="element-481">
                  <div className="text-mono-tiny uppercase mb-4" style={{
                color: 'var(--accent-500)',
                letterSpacing: '0.14em'
              }} data-id="element-482">
                    Risultati previsti
                  </div>
                  <ul className={`grid gap-x-8 gap-y-6 ${m.kpi.length > 1 ? 'sm:grid-cols-2' : 'grid-cols-1'}`} data-id="element-483">
                    {m.kpi.map((item, idx) => <li key={idx} className="pl-4" style={{
                  borderLeft: '2px solid var(--accent-500)'
                }} data-id="element-484">
                        <div className="tabular-nums" style={{
                    color: 'var(--accent-500)',
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(28px, 3.6vw, 34px)',
                    fontWeight: 500,
                    lineHeight: 1.0,
                    letterSpacing: '-0.02em'
                  }} data-id="element-485">
                          {item.value}
                        </div>
                        <div className="text-body-md color-ink-secondary mt-2 leading-snug" data-id="element-486">
                          {item.label}
                        </div>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.article>;
}
/* ─────────────────────────────────────────────────────────────
   MODULE FIELD — a single step row (badge + label + body)
   ───────────────────────────────────────────────────────────── */
interface ModuleFieldProps {
  step: string;
  label: string;
  body: string;
  accent: 'warm' | 'neutral' | 'lime';
  climax?: boolean;
}
function ModuleField({
  step,
  label,
  body,
  accent,
  climax = false
}: ModuleFieldProps) {
  const dotColor = accent === 'warm' ? 'var(--warm-500)' : accent === 'lime' ? 'var(--accent-500)' : 'var(--ink-secondary)';
  const labelColor = accent === 'warm' ? 'var(--warm-500)' : accent === 'lime' ? 'var(--accent-500)' : 'var(--ink-secondary)';
  return <div className="grid grid-cols-[40px_1fr] gap-x-4 items-start" data-id="element-487">
      {/* Numbered badge */}
      <div className="relative flex items-center justify-center rounded-full text-mono-tiny tabular-nums" style={{
      width: 28,
      height: 28,
      marginTop: 0,
      background: climax ? 'var(--accent-500)' : '#000000',
      border: climax ? '1px solid var(--accent-500)' : '1px solid rgba(255,255,255,0.22)',
      color: climax ? '#000000' : 'var(--ink-secondary)',
      letterSpacing: '0.04em',
      fontSize: 11,
      zIndex: 1
    }} aria-hidden="true" data-id="element-488">
        {step}
      </div>

      {/* Right column: label + body */}
      <div className="flex flex-col min-w-0 pt-[1px]" data-id="element-489">
        <div className="flex items-center gap-2 mb-2" data-id="element-490">
          <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
          background: dotColor
        }} data-id="element-491" />
          <span className="text-mono-tiny" style={{
          color: labelColor,
          letterSpacing: '0.14em'
        }} data-id="element-492">
            {label}
          </span>
        </div>

        {climax ? <div className="text-[20px] md:text-[22px] lg:text-[24px] font-[500]" style={{
        color: 'var(--accent-500)',
        fontFamily: 'var(--font-display)',
        lineHeight: 1.2,
        letterSpacing: '-0.015em'
      }} data-id="element-493">
            {body}
          </div> : <p className="text-body-md color-ink-secondary leading-relaxed" data-id="element-494">
            {body}
          </p>}
      </div>
    </div>;
}
/* ─────────────────────────────────────────────────────────────
   MILESTONE — circle marker on the rail
   ───────────────────────────────────────────────────────────── */
interface MilestoneProps {
  phase: Phase;
  isActive: boolean;
  size?: number;
}
function Milestone({
  phase,
  isActive,
  size = 48
}: MilestoneProps) {
  const Icon = phase.icon;
  // Hardcoded lime to bypass any CSS-variable resolution edge case.
  const LIME = '#d4ff3a';
  return <div className="relative flex items-center justify-center rounded-full" style={{
    width: size,
    height: size,
    zIndex: 2,
    backgroundColor: isActive ? LIME : '#000000',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: isActive ? LIME : 'rgba(255,255,255,0.25)',
    boxShadow: isActive ? `0 0 0 6px rgba(212,255,58,0.14), 0 0 24px rgba(212,255,58,0.35)` : '0 0 0 4px rgba(0,0,0,1)',
    transition: 'background-color 300ms ease, border-color 300ms ease, box-shadow 300ms ease'
  }} aria-hidden="true" data-id="element-495">
      <Icon className="w-[18px] h-[18px]" style={{
      color: isActive ? '#000000' : '#ffffff',
      transition: 'color 300ms ease'
    }} data-id="element-496" />
    </div>;
}
/* ─────────────────────────────────────────────────────────────
   PHASE BLOCK — desktop (with rail-aligned milestone)
   ───────────────────────────────────────────────────────────── */
interface PhaseBlockDesktopProps {
  phase: Phase;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  refCallback: (el: HTMLElement | null) => void;
  startIndex: number;
}
function PhaseBlockDesktop({
  phase,
  isActive,
  isExpanded,
  onToggle,
  refCallback,
  startIndex
}: PhaseBlockDesktopProps) {
  const panelId = `phase-panel-${phase.id}`;
  return <section ref={refCallback} id={phase.id} data-phase-id={phase.id} className="relative grid grid-cols-[80px_1fr] gap-x-8 scroll-mt-[120px]" data-id="element-497">
      {/* Left rail cell — milestone aligned to the top of the row */}
      <div className="relative flex justify-center pt-2" data-id="element-498">
        <Milestone phase={phase} isActive={isActive} data-id="element-499" />
      </div>

      {/* Right content cell — title aligned with icon */}
      <div className="relative pt-2 pb-2" data-id="element-500">
        <AnimatedSection data-id="element-501">
          <button type="button" onClick={onToggle} aria-expanded={isExpanded} aria-controls={panelId} className="group w-full text-left mb-8 lg:mb-10 flex items-start justify-between gap-6 cursor-pointer focus:outline-none" data-id="element-502">
            <div className="min-w-0" data-id="element-503">
              <h3 className="color-ink-primary mb-3 text-[28px] font-[500] max-w-[24ch] transition-colors duration-200 group-hover:color-accent" style={{
              fontFamily: 'var(--font-display)',
              lineHeight: 1.05,
              letterSpacing: '-0.03em'
            }} data-id="element-504">
                {phase.label}
              </h3>
              <p className="text-body-lg color-ink-secondary max-w-[64ch]" data-id="element-505">
                {phase.description}
              </p>
            </div>
            <span aria-hidden="true" className="flex-shrink-0 mt-3 w-9 h-9 flex items-center justify-center color-ink-secondary group-hover:color-accent transition-colors duration-200" data-id="element-506">
              <ChevronDown className="w-6 h-6 transition-transform duration-300" style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }} data-id="element-507" />
            </span>
          </button>
        </AnimatedSection>

        <AnimatePresence initial={false} data-id="element-508">
          {isExpanded && <motion.div id={panelId} key="panel" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          height: {
            duration: 0.35,
            ease: [0.16, 1, 0.3, 1]
          },
          opacity: {
            duration: 0.25
          }
        }} style={{
          overflow: 'hidden'
        }} data-id="element-509">
              {/* Module list — each module as a separate widget */}
              <div className="flex flex-col gap-4" data-id="element-510">
                {phase.modules.map((m, i) => <ModuleCard key={m.name} module={m} index={startIndex + i} data-id="element-511" />)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}
/* ─────────────────────────────────────────────────────────────
   PHASE BLOCK — mobile (rail on left, single column)
   ───────────────────────────────────────────────────────────── */
interface PhaseBlockMobileProps {
  phase: Phase;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  refCallback: (el: HTMLElement | null) => void;
  startIndex: number;
}
function PhaseBlockMobile({
  phase,
  isActive,
  isExpanded,
  onToggle,
  refCallback,
  startIndex
}: PhaseBlockMobileProps) {
  const panelId = `phase-panel-m-${phase.id}`;
  return <section ref={refCallback} id={`${phase.id}-m`} data-phase-id={phase.id} className="relative scroll-mt-[100px]" data-id="element-512">
      {/* Phase header — toggles the panel */}
      <button type="button" onClick={onToggle} aria-expanded={isExpanded} aria-controls={panelId} className="group w-full text-left mb-6 flex items-start justify-between gap-4 cursor-pointer focus:outline-none" data-id="element-513">
        <div className="min-w-0" data-id="element-514">
          <h3 className="color-ink-primary mb-3 text-[28px] font-[500] transition-colors duration-200 group-hover:color-accent" style={{
          fontFamily: 'var(--font-display)',
          lineHeight: 1.05,
          letterSpacing: '-0.03em'
        }} data-id="element-515">
            {phase.label}
          </h3>
          <p className="text-body-md color-ink-secondary" data-id="element-516">
            {phase.description}
          </p>
        </div>
        <span aria-hidden="true" className="flex-shrink-0 mt-1 w-8 h-8 flex items-center justify-center color-ink-secondary group-hover:color-accent transition-colors duration-200" data-id="element-517">
          <ChevronDown className="w-5 h-5 transition-transform duration-300" style={{
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
        }} data-id="element-518" />
        </span>
      </button>

      <AnimatePresence initial={false} data-id="element-519">
        {isExpanded && <motion.div id={panelId} key="panel" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        height: {
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        },
        opacity: {
          duration: 0.25
        }
      }} style={{
        overflow: 'hidden'
      }} data-id="element-520">
            {/* Module list — each module as a separate widget */}
            <div className="flex flex-col gap-4" data-id="element-521">
              {phase.modules.map((m, i) => <ModuleCard key={m.name} module={m} index={startIndex + i} data-id="element-522" />)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}
/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */
export function SolutionIntro() {
  // Flatten all modules from all phases into a single ordered list.
  const allModules = phases.flatMap(p => p.modules);
  return <section className="section-pad" id="prodotto" data-id="element-523">
      <div className="wrap" data-id="element-524">
        {/* ─────── SOLUTION HEADLINE ─────── */}
        <AnimatedSection data-id="element-525">
          <div className="mb-16 lg:mb-20" data-id="element-526">
            <div className="section-tag section-tag--accent mb-6" data-id="element-527">
              LA SOLUZIONE
            </div>
            <h2 className="text-display-section mb-5 max-w-[32ch]" data-id="element-528">
              Aisac integra{' '}
              <strong className="color-ink-primary font-medium" data-id="element-529">
                funzionalità intelligenti
              </strong>{' '}
              in ogni fase della delivery. E la rende più efficiente.
            </h2>
            <p className="text-body-lg color-ink-secondary max-w-[58ch]" data-id="element-530">
              Quattro momenti del journey, nove moduli, un'unica esperienza
              presidiata.
            </p>
          </div>
        </AnimatedSection>

        {/* ─────── "COME FUNZIONA" — module list intro ─────── */}
        <AnimatedSection data-id="element-531">
          <div className="mb-10 lg:mb-12 pt-8 lg:pt-10 border-t border-default" data-id="element-532">
            <div className="section-tag section-tag--accent mb-6" data-id="element-533">
              COME FUNZIONA
            </div>
            <h3 className="color-ink-primary max-w-[36ch]" style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(28px, 3.4vw, 40px)',
            fontWeight: 500,
            lineHeight: 1.1,
            letterSpacing: '-0.02em'
          }} data-id="element-534">
              9 moduli: Ognuno risolve un problema. Ognuno riduce un costo.
            </h3>
          </div>
        </AnimatedSection>

        {/* ─────── FLAT MODULE LIST ─────── */}
        <div className="flex flex-col gap-4" data-id="element-535">
          {allModules.map((m, i) => <ModuleCard key={m.name} module={m} index={i} data-id="element-536" />)}
        </div>
      </div>
    </section>;
}